import { Stack, styled, Typography, useTheme } from "@mui/material";
import React from "react";
import { toCSV } from "react-csv/lib/core";
import Tooltip from "@hyro-components/Tooltip";
import { DOWNLOAD_BTN_SIZES, DOWNLOAD_BTN_VARIANTS } from "@consts";
import { downloadFile, escapeCsvCell, sanitizeCsvRows } from "@utils";

import DownloadIcon from "../../icons/DownloadIcon";

const DownloadLink = styled("a")(({ disabled, variant, size, theme }) => ({
  textDecoration: "none",
  cursor: "pointer",
  backgroundColor: theme.palette.system[0],
  ...(variant === DOWNLOAD_BTN_VARIANTS.OUTLINED && {
    border: "1px solid rgba(45, 44, 68, 0.24)",
    borderRadius: "8px",
    padding: size === DOWNLOAD_BTN_SIZES.LARGE ? "9px" : "7px",
    "&:hover": {
      backgroundColor: "#EBEBFD",
      borderColor: "#7583F7"
    }
  }),
  ...(disabled && {
    pointerEvents: "none",
    cursor: "default",
    border: "1px solid rgba(45, 44, 68, 0.12)",
    backgroundColor: theme.palette.grey[50]
  })
}));

// Neutralize CSV formula-injection (CWE-1236) before serializing.
const sanitize = (data) => (Array.isArray(data) ? sanitizeCsvRows(data) : data);

const DownloadButton = ({ fileName, fileHeaders, fileData, buttonText = "", disabled, variant = DOWNLOAD_BTN_VARIANTS.TEXT, size = DOWNLOAD_BTN_SIZES.MEDIUM, testId }) => {
  const [hover, setHover] = React.useState(false);
  const theme = useTheme();
  const safeHeaders = Array.isArray(fileHeaders)
    ? fileHeaders.map((header) =>
      header && typeof header === "object"
        ? { ...header, label: escapeCsvCell(header.label) }
        : escapeCsvCell(header))
    : fileHeaders;

  // The CSV is built only on click — react-csv's CSVLink serializes its data
  // prop into the href on every render, which is unusable for large datasets.
  // fileData may be the rows array or a function returning it.
  const handleDownload = (event) => {
    event.preventDefault();
    const rows = typeof fileData === "function" ? fileData() : fileData;
    const csv = toCSV(sanitize(rows), safeHeaders);
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    downloadFile(url, `${fileName}.csv`);
    URL.revokeObjectURL(url);
  };

  return (
    <Tooltip title={disabled || Boolean(buttonText) ? "" : "Download"}>
      <DownloadLink
        onClick={handleDownload}
        disabled={disabled}
        variant={variant}
        size={size}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid={testId}>
        <Stack
          gap={1}
          direction='row'
          alignItems='center'
        >
          <DownloadIcon stroke={disabled ? theme.palette.text[600] : hover ? theme.palette.primary[500] : theme.palette.system[700]} />
          {!!buttonText && <Typography
            sx={{ color: disabled ? theme.palette.text[600] : hover ? theme.palette.primary[500] : theme.palette.text[800] }}
            variant='buttonMedium'>
            {buttonText}
          </Typography>}
        </Stack>
      </DownloadLink>
    </Tooltip>
  );
};

export default DownloadButton;
